<template>
  <div :class="['SellingAction', parentContext, { hidden: !showDropdown && !showOverlay }]">
    <transition name="fade-scale">
      <div
        v-if="showDropdown && !mediaOnly"
        class="actions-menu"
        v-click-outside="closeMenu"
        @click.stop
      >
        <!-- Product Menu Item -->
        <div
          class="menu-item"
          @click="openProductOverlay"
          :class="{ pressed: selectedCard === 'Product' }"
        >
          <svg class="tinyImg">
            <use href="@/assets/icons/iconset.svg#product-tiny"></use>
          </svg>
          Product
        </div>

        <!-- Service Menu Item -->
        <div
          class="menu-item"
          @click="openServiceOverlay"
          :class="{ pressed: selectedCard === 'Service' }"
        >
          <svg class="tinyImg">
            <use href="@/assets/icons/iconset.svg#service-tiny"></use>
          </svg>
          Service
        </div>
      </div>
    </transition>

    <!-- Add Product Overlay -->
    <Overlay
      :visible="showOverlay && overlayType === 'Product'"
      :title="dynamicTitleProduct"
      :smallTextAfterTitle="postingAsLabel"
      :showBackArrow="shouldShowBackArrow"
      :currentStep="currentStep"
      @close="closeOverlay"
      @previousStep="goToPreviousStep"
    >
      <template #before-buttons>
        <AddProductForm
          ref="addProductFormRef"
          :userUnitsOrg="userUnitsOrg"
          :userCredentials="userCredentials"
          :currentStep="currentStep"
          @productAddedSuccessfully="handleProductAdded"
          @update-title="handleTitleUpdate"
          @save-request="triggerAddProduct"
          @update-canPreview="canPreview = $event"
          @update-currentStep="handleStepUpdate('product')"
        />
      </template>
      <div class="row alignCentreRow">
        <UnitButton
          @click="closeOverlay"
          v-if="!isSaveLoading"
          variant="secondary"
        >
          Cancel
        </UnitButton>
        <UnitButton
          v-if="currentStep !== 'preview'"
          variant="secondary"
          @click="validateFormAndProceed('Product')"
        >
          Next
        </UnitButton>
        <UnitButton
          @click="saveProduct"
          variant="secondary"
          v-if="user.loggedIn && currentStep === 'preview' && !isSaveLoading"
        >
          Save
        </UnitButton>
        <UnitButton v-if="isSaveLoading" variant="secondary" disabled>
          Processing...
        </UnitButton>
      </div>
    </Overlay>

    <!-- Add Service Overlay -->
    <Overlay
      :visible="showOverlay && overlayType === 'Service'"
      :title="dynamicTitleService"
      :smallTextAfterTitle="postingAsLabel"
      :showBackArrow="shouldShowBackArrow"
      :currentStep="currentStep"
      @close="closeOverlay"
      @previousStep="goToPreviousStep"
    >
      <template #before-buttons>
        <AddServiceForm
          v-if="overlayType === 'Service'"
          ref="addServiceFormRef"
          :user="user"
          :userCredentials="userCredentials"
          :userUnitsOrg="userUnitsOrg"
          :currentStep="currentStep"
          @serviceAddedSuccessfully="handleServiceAdded"
          @update-title="handleTitleUpdate"
          @save-request="triggerAddService"
          @update-canPreview="canPreview = $event"
          @update-currentStep="handleStepUpdate('service')"
        />
      </template>
      <div class="row alignCentreRow">
        <UnitButton
          variant="secondary"
          @click="closeOverlay"
          v-if="!isSaveLoading"
        >
          Cancel
        </UnitButton>
        <UnitButton
          v-if="currentStep !== 'preview'"
          variant="secondary"
          @click="validateFormAndProceed('Service', true)"
        >
          Next
        </UnitButton>
        <UnitButton
          @click="saveService"
          variant="secondary"
          v-if="user.loggedIn && currentStep === 'preview' && !isSaveLoading"
        >
          Save
        </UnitButton>
        <UnitButton v-if="isSaveLoading" variant="secondary" disabled>
          Processing...
        </UnitButton>
      </div>
    </Overlay>

    <!-- Add Media Overlay -->
    <Overlay
      :visible="showOverlay && overlayType === 'Media'"
      :title="mediaOverlayTitle"
      :smallTextAfterTitle="postingAsLabel"
      :showBackArrow="shouldShowBackArrow"
      :currentStep="currentStep"
      @close="closeOverlay"
      @previousStep="goToPreviousStep"
    >
      <template #before-buttons>
        <!-- Mount PostComposer only when Media overlay is active to avoid stale internal state -->
        <PostComposer
          v-if="overlayType === 'Media' && showOverlay"
          :key="composerKey"
          ref="postComposerRef"
          :isProcessing="isSaveLoading"
          @update-canPreview="canPreview = $event"
          @next="currentStep = 'preview'"
          @submit="saveMedia"
          @cancel="closeOverlay"
          @close="closeOverlay"
          @open-sale="handleOpenSale"
        />
      </template>
      <!-- Composer renders its own inline Save/Processing in preview; no duplicate footer buttons here. -->
    </Overlay>

    <!-- Add Multimedia Overlay -->
    <Overlay
      :visible="showOverlay && overlayType === 'Multimedia'"
      title="Add Multimedia post"
      :smallTextAfterTitle="postingAsLabel"
      :showBackArrow="shouldShowBackArrow"
      :currentStep="currentStep"
      @close="closeOverlay"
      @previousStep="goToPreviousStep"
    >
      <template #before-buttons>
        <AddMultimediaForm
          ref="addMultimediaFormRef"
          :user="user"
          :userUnitsOrg="userUnitsOrg"
          :currentStep="currentStep"
          @postAddedSuccessfully="handleMultimediaAdded"
          @update-title="handleTitleUpdate"
          @save-request="triggerAddMultimedia"
          @update-canPreview="canPreview = $event"
          @update-currentStep="handleStepUpdate('post')"
        />
      </template>
      <div class="row alignCentreRow">
        <UnitButton
          @click="closeOverlay"
          v-if="!isSaveLoading"
          variant="secondary"
        >
          Cancel
        </UnitButton>
        <UnitButton
          v-if="currentStep !== 'preview'"
          variant="secondary"
          @click="validateFormAndProceed('Multimedia')"
        >
          Next
        </UnitButton>
        <UnitButton
          @click="saveMultimedia"
          variant="secondary"
          v-if="user.loggedIn && currentStep === 'preview' && !isSaveLoading"
        >
          Save
        </UnitButton>
        <UnitButton v-if="isSaveLoading" variant="secondary" disabled>
          Processing...
        </UnitButton>
      </div>
    </Overlay>
  </div>
</template>

<script>
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { computed, ref, watch } from "vue";
import { toast } from "vue3-toastify";

import Overlay from "@/components/GenericComponents/Overlay.vue";
import AddProductForm from "@/components/DashboardComponents/EcosystemComponents/AddProductForm.vue";
import AddServiceForm from "@/components/DashboardComponents/EcosystemComponents/AddServiceForm.vue";
import PostComposer from "@/components/PostComposer/PostComposer.vue";
import AddMultimediaForm from "@/components/DashboardComponents/EcosystemComponents/AddMultimediaForm.vue";
import ImageUpload from "@/components/GenericComponents/ImageUpload.vue";
import { uploadMediaFile, uploadImageWithVariants } from "@/utils/uploadUtils";
import { cleanupChangedFiles } from "@/utils/storageCleanup";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";

export default {
  name: "SellingAction",
  components: {
    Overlay,
    AddProductForm,
    AddServiceForm,
    ImageUpload,
    AddMultimediaForm,
    PostComposer,
    UnitButton,
  },
  props: {
    userCredentials: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    userUnitsOrg: {
      type: Object,
      required: true,
    },
    userIsInAnOrg: {
      type: Boolean,
      required: true,
    },
    parentContext: {
      type: String,
      default: "Ecosystem",
    },
    // With item reads on, products and services are created through the
    // private item composer; this component then serves media posts only.
    mediaOnly: {
      type: Boolean,
      default: false,
    },
    showDropdown: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const router = useRouter();
    const selectedCard = ref(null);
    let triggerAddProduct;
    let triggerAddService;
    const showOverlay = ref(false);
    const overlayType = ref("");
    const currentStep = ref("initial");
    const postComposerRef = ref(null);
    const currentProduct = ref(null);

    const isSaveLoading = ref(false);
    const addProductFormRef = ref(null);
    const addServiceFormRef = ref(null);
    const canPreview = ref(false);
    const dynamicTitle = ref("Edit details");
    const dynamicTitleProduct = ref("Add Product");
    const dynamicTitleService = ref("Add Service");
    const mediaOverlayTitle = ref("Add Media Post");
    const currentOrgUID = computed(() => store.getters.currentOrgUID);
    const activeOrgName = computed(
      () =>
        props.userUnitsOrg?.[currentOrgUID.value]?.organisationName ||
        props.userUnitsOrg?.[currentOrgUID.value]?.name ||
        store.getters.selectedOrgMembership?.organisationName ||
        "",
    );
    const postingAsLabel = computed(() =>
      activeOrgName.value ? `Posting as ${activeOrgName.value}` : "Select an organisation to post",
    );
    // Force a fresh PostComposer instance per open/edit session to clear prior images/content
    const composerKey = ref(`composer_${Date.now()}`);

    const addMultimediaFormRef = ref(null);

    const triggerAddMultimedia = () => {
      if (addMultimediaFormRef.value) {
        addMultimediaFormRef.value.submitPost();
      }
    };

    const openMultimediaOverlay = () => {
      openOverlay("Multimedia");
    };

    const openMediaOverlay = (product = null) => {
      // Set the title based on whether we're editing or creating
      if (product && product.id) {
        currentProduct.value = product;
        mediaOverlayTitle.value = "Edit Post Details";
      } else {
        currentProduct.value = null;
        mediaOverlayTitle.value = "Add Media Post";
      }
      // Bump the composer key so the component remounts with a clean state
      composerKey.value = `${
        product?.id ? `edit_${product.id}` : "new"
      }_${Date.now()}`;
      openOverlay("Media");
    };

    const closeMenu = () => {
      emit("closeSellingAction"); // Emit event to parent to close the dropdown
    };

    // Handle open-sale event from PostComposer - open mint modal with draft data (NO save yet)
    const handleOpenSale = async () => {
      console.log(
        "[SellingAction] handleOpenSale: opening mint modal with draft data",
      );

      // Collect draft data from composer (don't save yet - save happens after mint config)
      try {
        if (!postComposerRef.value) {
          console.warn("[SellingAction] No postComposerRef available");
          return;
        }

        const composer = postComposerRef.value;

        // collectDraft now returns everything we need (images, title, caption, previewImage)
        const draft = await composer.collectDraft?.();

        console.log("[SellingAction] Draft data from collectDraft:", {
          title: draft?.title,
          imagesCount: draft?.imagesCount,
          hasPreviewImage: !!draft?.previewImage,
          selectedType: draft?.selectedType,
        });

        if (!draft) {
          console.warn("[SellingAction] collectDraft returned nothing");
          return;
        }

        // Extract values from draft
        const images = draft.images || [];
        const title = draft.title || "";
        const caption = draft.caption || "";
        const selectedType = draft.selectedType || "photos";
        const previewImage = draft.previewImage || "";

        // Dispatch event with draft data - MasonryMarketplace will open SimpleMintModal
        window.dispatchEvent(
          new CustomEvent("marketplace:open-mint-modal", {
            detail: {
              // Draft mode - no itemId means we need to save first when confirming
              mode: "draft",
              draft: {
                title,
                caption,
                images,
                selectedType,
                previewImage,
              },
              // Pass ref to composer so we can save from the modal
              composerRef: postComposerRef.value,
              ownerId: ownerId.value,
              organisationId: currentOrgUID.value,
            },
          }),
        );
      } catch (e) {
        console.error("[SellingAction] Failed to prepare mint modal data:", e);
        window.$toast?.error?.(
          "Failed to open mint settings. Please try again.",
        );
      }
    };

    const selectCard = (type) => {
      if (type === "Product" || type === "Service") {
        openOverlay(type);
      }
      if (selectedCard.value === type) {
        selectedCard.value = null;
      } else {
        selectedCard.value = type;
      }
    };

    const validateFormAndProceed = (type) => {
      console.log(`validateFormAndProceed called for: ${type}`);

      let isValid = false;

      if (type === "Product" && addProductFormRef.value) {
        isValid = addProductFormRef.value.handleFormValidation();
      } else if (type === "Service" && addServiceFormRef.value) {
        isValid = addServiceFormRef.value.handleFormValidation();
      } else if (type === "Media") {
        // With PostComposer, canPreview indicates whether we have enough to proceed
        isValid = !!canPreview.value;
        console.log("[SellingAction] Media composer canPreview:", isValid);
        if (!isValid) return;
      } else if (type === "Multimedia" && addMultimediaFormRef.value) {
        console.log("Calling validateForm on addMultimediaFormRef...");
        isValid = addMultimediaFormRef.value.validateForm();

        if (!isValid) {
          console.error("Validation failed for multimedia form.");
          return;
        }

        console.log("Validation passed. Updating multimedia form step.");
      }

      if (isValid) {
        console.log(`Setting currentStep to preview for ${type}`);
        currentStep.value = "preview";
      }
    };
    const orgUid = computed(() => store.getters.selectedOrgUID || null);
    const mediaLoading = computed(() => store.getters.mediaLoading);

    const handleProductAdded = () => {
      isSaveLoading.value = false;
      store.dispatch("fetchProducts", { orgUid: orgUid.value });
      toast.success("Product added successfully!");

      emit("itemEdited");
      closeOverlay();
      // Notify timeline-based views to refresh
      window.dispatchEvent(new CustomEvent("marketplace:new-media"));
    };

    const handleServiceAdded = () => {
      isSaveLoading.value = false;
      store.dispatch("fetchServices", { orgUid: orgUid.value });
      toast.success("Service added successfully!");
      emit("itemEdited");
      closeOverlay();
      // Notify timeline-based views to refresh
      window.dispatchEvent(new CustomEvent("marketplace:new-media"));
    };

    const handleMultimediaAdded = () => {
      isSaveLoading.value = false;
      store.dispatch("fetchMultimediaPosts"); // Refresh multimedia posts list
      toast.success("Multimedia post added successfully!");
      closeOverlay();
    };

    const handleTitleUpdate = (newTitle) => {
      if (overlayType.value === "Product") {
        dynamicTitleProduct.value = newTitle;
      } else if (overlayType.value === "Service") {
        dynamicTitleService.value = newTitle;
      } else if (overlayType.value === "Multimedia") {
        dynamicTitle.value = newTitle; // Make sure this updates correctly
      }
    };

    // Separate handler for media title to avoid changing overlay title
    const handleMediaTitleUpdate = (newTitle) => {
      // Don't update overlay title, just log for debugging
      console.log("[SellingAction] Media title updated:", newTitle);
    };

    const ownerId = computed(
      () =>
        props.userCredentials?.data?.uid ||
        store.getters.user?.data.uid ||
        null,
    );
    const handleMediaAdded = async (mediaData) => {
      console.log("[SellingAction] handleMediaAdded received:", mediaData);
      // Ensure owner is defined:
      isSaveLoading.value = true;

      try {
        // Process new files (if any)
        let imageUrls = [];
        let variantsToPersist = null;

        // Check if we have multiple files (images)
        if (mediaData.files && mediaData.files.length > 0) {
          console.log(
            "[SellingAction] Processing multiple image files:",
            mediaData.files.length,
          );

          // Filter out files that are already strings (URLs) to avoid upload errors
          const filesToUpload = mediaData.files.filter(
            (file) => file instanceof File && !file.isExistingFile,
          );

          // Collect existing URLs to preserve them
          const existingUrls = mediaData.files
            .filter((file) => typeof file === "string" || file.isExistingFile)
            .map((file) => (typeof file === "string" ? file : file.url));

          console.log(
            `[SellingAction] Found ${filesToUpload.length} new files to upload`,
          );
          console.log(
            `[SellingAction] Found ${existingUrls.length} existing URLs to preserve`,
          );

          // Add existing URLs first
          imageUrls.push(...existingUrls);

          // Then upload new files
          if (filesToUpload.length > 0) {
            // Upload first file with variants (thumbnail will use this)
            const primary = filesToUpload[0];
            let primaryRes = null;
            try {
              primaryRes = await uploadImageWithVariants(primary, {
                folder: `images/${ownerId.value || "users"}`,
                ownerUid: ownerId.value,
              });
              imageUrls.push(primaryRes.url);
            } catch (e) {
              console.warn(
                "[SellingAction] Variant upload failed, falling back",
                e,
              );
              const fallbackUrl = await uploadMediaFile(primary, {
                ownerUid: ownerId.value,
              });
              imageUrls.push(fallbackUrl);
            }

            // Upload remaining files without variants for speed
            const rest = filesToUpload.slice(1);
            if (rest.length) {
              const restUrls = await Promise.all(
                rest.map((f) =>
                  uploadMediaFile(f, { ownerUid: ownerId.value }),
                ),
              );
              imageUrls.push(...restUrls);
            }

            // Stash variants on payload to be saved
            if (primaryRes && primaryRes.variants) {
              // We'll attach to payload after it's built below
              mediaData._primaryVariants = primaryRes.variants;
            }

            console.log(
              "[SellingAction] Uploaded files (with variants for first)",
              imageUrls,
            );
          }
        }
        // Handle single file upload (backwards compatibility)
        else if (
          mediaData.file instanceof File &&
          !mediaData.file.isExistingFile
        ) {
          try {
            console.log(
              "[SellingAction] Uploading single image with variants:",
              mediaData.file,
            );
            const primaryRes = await uploadImageWithVariants(mediaData.file, {
              folder: `images/${ownerId.value || "users"}`,
              ownerUid: ownerId.value,
            });
            imageUrls.push(primaryRes.url);
            // Stash variants for save
            if (primaryRes && primaryRes.variants) {
              mediaData._primaryVariants = primaryRes.variants;
            }
          } catch (uploadError) {
            console.warn(
              "[SellingAction] Variant upload failed, falling back to generic uploader:",
              uploadError,
            );
            try {
              const uploadedUrl = await uploadMediaFile(mediaData.file, {
                ownerUid: ownerId.value,
              });
              imageUrls.push(uploadedUrl);
            } catch (fallbackErr) {
              console.error(
                "Upload error (fallback also failed):",
                fallbackErr,
              );
            }
          }
        }

        // Handle existing mediaUrl if present (backwards compatibility)
        else if (
          mediaData.mediaUrl &&
          !imageUrls.includes(mediaData.mediaUrl)
        ) {
          console.log(
            "[SellingAction] Using existing mediaUrl (will generate variants if missing):",
            mediaData.mediaUrl,
          );
          imageUrls.push(mediaData.mediaUrl);
          // If editing and variants are missing, generate them
          if (
            !mediaData.variants ||
            Object.keys(mediaData.variants).length === 0
          ) {
            try {
              // Fetch the image as a Blob and convert to File
              const response = await fetch(mediaData.mediaUrl);
              const blob = await response.blob();
              // Try to infer a filename from the URL
              const urlParts = mediaData.mediaUrl.split("/");
              const filename =
                urlParts[urlParts.length - 1].split("?")[0] || "image.jpg";
              const file = new File([blob], filename, {
                type: blob.type || "image/jpeg",
              });
              const variantRes = await uploadImageWithVariants(file, {
                folder: `images/${ownerId.value || "users"}`,
                ownerUid: ownerId.value,
              });
              variantsToPersist = variantRes.variants;
              // Optionally, update imageUrls[0] to the new largest variant if you want to replace the original
              // imageUrls[0] = variantRes.url;
            } catch (err) {
              console.warn(
                "[SellingAction] Failed to generate variants for existing mediaUrl:",
                err,
              );
            }
          }
        }

        // Filter out any empty values and remove duplicates
        imageUrls = [...new Set(imageUrls)].filter(Boolean);

        console.log("[SellingAction] Final image URLs:", imageUrls);

        // VALIDATION: Prevent accidental file removal without content
        if (mediaData.id && imageUrls.length === 0) {
          const hasTitle = mediaData.itemName && mediaData.itemName.trim();
          const hasCaption = mediaData.caption && mediaData.caption.trim();

          if (!hasTitle && !hasCaption) {
            // No files and no text content - ask user what to do
            const shouldDelete = window.confirm(
              "This post will have no content (no files, title, or caption). Do you want to delete the post entirely?\n\nClick OK to delete the post, or Cancel to go back and add content.",
            );

            if (shouldDelete) {
              // User wants to delete the entire post
              try {
                await store.dispatch("mediaActions/deleteItem", {
                  id: mediaData.id,
                  type: "media",
                  mediaType: mediaData.mediaType,
                  images: [], // Will be fetched from current document
                });
                toast.success("Post deleted successfully!");
                closeOverlay();
                return;
              } catch (error) {
                console.error("Error deleting post:", error);
                toast.error("Failed to delete post: " + error.message);
                return;
              }
            } else {
              // User wants to go back and add content
              isSaveLoading.value = false;
              return;
            }
          }
        }

        // Ensure we have the owner
        const ownerUid =
          ownerId.value || mediaData.owner || store.getters.user?.data?.uid;

        console.log("[SellingAction] Owner UID for media post:", ownerUid);

        // Determine the media type - auto-derive when files removed
        let mediaType = mediaData.mediaType || "image";

        // Embed posts keep their mediaType — skip text derivation
        const isEmbed =
          mediaType === "embed" || !!mediaData.embedHtml;
        if (isEmbed) {
          mediaType = "embed";
        }

        // Auto-update mediaType when all files removed but text remains
        if (
          !isEmbed &&
          imageUrls.length === 0 &&
          !mediaData.mediaUrl &&
          !mediaData.audioUrl &&
          !mediaData.documentUrls?.length
        ) {
          const hasTitle = mediaData.itemName && mediaData.itemName.trim();
          const hasCaption = mediaData.caption && mediaData.caption.trim();

          if (hasTitle && !hasCaption) {
            mediaType = "poster";
          } else if (!hasTitle && hasCaption) {
            mediaType =
              mediaData.caption.trim().length <= 140 ? "tweet" : "post";
          } else if (hasTitle && hasCaption) {
            const combined = `${mediaData.itemName.trim()} ${mediaData.caption.trim()}`;
            mediaType = combined.length <= 140 ? "tweet" : "post";
          }

          console.log(
            "[SellingAction] Auto-derived mediaType for text-only:",
            mediaType,
          );
        }

        // Prepare the payload data
        const payloadData = {
          itemName: mediaData.itemName,
          caption: mediaData.caption,
          images: imageUrls,
          owner: ownerUid,
          organisation: currentOrgUID.value,
          mediaType: mediaType,
          type: "media",
          thumbnailIndex: 0,
        };

        // Persist embed fields for embed posts
        if (isEmbed) {
          if (mediaData.embedHtml) payloadData.embedHtml = mediaData.embedHtml;
          if (mediaData.embedUrl) payloadData.embedUrl = mediaData.embedUrl;
          if (mediaData.embedProvider)
            payloadData.embedProvider = mediaData.embedProvider;
          if (mediaData.thumbnailUrl)
            payloadData.thumbnailUrl = mediaData.thumbnailUrl;
        }

        // Attach variants if available (from new upload or from existing image)
        if (mediaData._primaryVariants) {
          payloadData.variants = mediaData._primaryVariants;
        } else if (variantsToPersist) {
          payloadData.variants = variantsToPersist;
        }

        // Add ID if we're editing an existing item
        if (mediaData.id) {
          payloadData.id = mediaData.id;
        }

        console.log("[SellingAction] Sending payload to store:", payloadData);

        // Now dispatch the action with the standardized data
        let result;
        try {
          if (mediaData.id) {
            // For editing - try both namespaced and non-namespaced approaches
            try {
              await store.dispatch("mediaActions/updateMediaItem", {
                id: mediaData.id,
                collection:
                  mediaType === "image" || mediaType === "embed"
                    ? "images"
                    : mediaType === "video"
                    ? "videos"
                    : mediaType === "documents"
                    ? "images"
                    : "audios",
                data: payloadData,
              });
            } catch (err) {
              console.log(
                "Failed with namespaced action, trying non-namespaced:",
                err,
              );
              await store.dispatch("updateMedia", {
                id: mediaData.id,
                collection:
                  mediaType === "image" || mediaType === "embed"
                    ? "images"
                    : mediaType === "video"
                    ? "videos"
                    : mediaType === "documents"
                    ? "images"
                    : "audios",
                data: payloadData,
              });
            }
          } else {
            // For creating - try both approaches to ensure compatibility
            try {
              result = await store.dispatch(
                "mediaActions/addMediaPost",
                payloadData,
              );
            } catch (err) {
              console.log(
                "Failed with namespaced action, trying non-namespaced:",
                err,
              );
              result = await store.dispatch("addMedia", payloadData);
            }

            if (result && result.id) {
              console.log("Media saved with real ID:", result.id);
            }
          }
        } catch (error) {
          console.log("Error saving media:", error);
          throw error; // Re-throw to be handled by the outer catch
        }

        toast.success("Media post added successfully!");
        closeOverlay();
      } catch (error) {
        console.error("Error in handleMediaAdded:", error);
        toast.error("Failed to add media post: " + error.message);
      } finally {
        isSaveLoading.value = false;
      }
    };
    // Also implement handleCaptionUpdate similarly:
    const handleCaptionUpdate = (newCaption) => {
      // console.log("[SellingAction] Caption updated:", newCaption);
    };

    const saveMedia = async () => {
      try {
        isSaveLoading.value = true;
        const owner = ownerId.value;
        const organisation = currentOrgUID.value;
        if (!postComposerRef.value) throw new Error("Composer not ready");
        // Optional: quick preflight – block obvious empty drafts
        try {
          const draft = await postComposerRef.value.collectDraft?.();
          const noText = !draft?.title && !draft?.caption;
          const noMedia =
            !draft?.imagesCount && !draft?.poster && !draft?.hasPreview;
          if (noText && noMedia) {
            toast.error(
              "Please add an image, a title, or a caption to publish.",
            );
            isSaveLoading.value = false;
            return null;
          }
        } catch {}
        // Publish in background if supported: show global micro progress bar + completion toast
        if (typeof postComposerRef.value.submitInBackground === "function") {
          postComposerRef.value.submitInBackground({ owner, organisation });
          toast.info(
            "Publishing in the background. You can close this while it finishes.",
            { autoClose: 2500 },
          );
          closeOverlay({ skipDirtyConfirm: true });
          return null;
        }

        // Fallback: legacy synchronous save - returns { id, collection } from PostComposer.save()
        const result = await postComposerRef.value.save({
          owner,
          organisation,
        });
        toast.success("Media post added successfully!");
        closeOverlay();
        return result;
      } catch (error) {
        console.error("Error saving media via composer:", error);
        toast.error("Failed to add media post: " + (error?.message || error));
        throw error; // Re-throw so handleOpenSale can catch it
      } finally {
        isSaveLoading.value = false;
      }
    };

    const openOverlay = (type) => {
      if (!currentOrgUID.value) {
        toast.error("Select an organisation before posting.");
        return;
      }
      overlayType.value = type;
      showOverlay.value = true;
      currentStep.value = "initial";
      store.dispatch("toggleNavbarVisibility", false); // Hide the Navbar
    };

    const openBatchMintModal = () => {
      selectedCard.value = "BatchMint";
      emit("closeSellingAction"); // Close the selling action dropdown
      // Dispatch global event to open batch mint modal at a higher level
      window.dispatchEvent(new CustomEvent("marketplace:open-batch-mint"));
    };

    const handleBatchMintSuccess = (result) => {
      console.log("[SellingAction] Batch mint success:", result);
      selectedCard.value = null;
      toast.success(
        `Minted ${result.tokenIds?.length || 0} NFTs successfully!`,
      );
      emit("itemEdited"); // Refresh the marketplace
    };

    const closeOverlay = ({ skipDirtyConfirm = false } = {}) => {
      // If closing Media overlay, warn when composer has a draft
      if (
        !skipDirtyConfirm &&
        overlayType.value === "Media" &&
        postComposerRef.value
      ) {
        try {
          const dirty = !!postComposerRef.value.isDirty?.value;
          if (dirty) {
            const ok = window.confirm("Discard your post?");
            if (!ok) return; // abort close
            postComposerRef.value.resetDraft?.();
            try {
              toast.warning("Draft cleared");
            } catch {}
          }
        } catch (e) {
          console.warn("[SellingAction] closeOverlay dirty-check failed", e);
        }
      }
      // Reset important state variables first
      currentProduct.value = null;

      // Reset all refs to the currentStep before resetting forms
      currentStep.value = "initial";

      // Clear form data for all forms
      console.log(
        `Closing overlay - resetting all forms. Current overlay: ${overlayType.value}`,
      );

      // Media composer state is ephemeral; no explicit reset needed

      // Reset other forms
      if (addServiceFormRef.value) {
        console.log("Resetting service form");
        addServiceFormRef.value.resetForm();
      }
      if (addProductFormRef.value) {
        console.log("Resetting product form");
        addProductFormRef.value.resetForm();
      }
      if (addMultimediaFormRef.value) {
        console.log("Resetting multimedia form");
        addMultimediaFormRef.value.resetForm();
      }

      // Update UI state
      store.dispatch("toggleNavbarVisibility", true); // Show the Navbar
      showOverlay.value = false;
      currentStep.value = "initial";
      selectedCard.value = null;

      // Reset overlay type (do this after handling the forms)
      overlayType.value = "";

      // Reset titles
      mediaOverlayTitle.value = "Add Media Post";
      dynamicTitleProduct.value = "Add Product";
      dynamicTitleService.value = "Add Service";

      isSaveLoading.value = false;

      // Prepare a fresh composer instance for next open
      composerKey.value = `composer_${Date.now()}`;

      // Final backup no-op for composer
    };

    const goToNextStep = () => {
      if (overlayType.value === "Product" && addProductFormRef.value) {
        if (!addProductFormRef.value.handleFormValidation()) {
          return;
        }
      } else if (overlayType.value === "Service" && addServiceFormRef.value) {
        if (!addServiceFormRef.value.handleFormValidation()) {
          return;
        }
      }

      if (overlayType.value === "Product" && currentStep.value === "initial") {
        addProductFormRef.value.gatherDataForPreview();
        currentStep.value = "preview";
      } else if (overlayType.value === "Service") {
        if (currentStep.value === "initial") {
          currentStep.value = "availability";
        } else if (currentStep.value === "availability") {
          if (
            addServiceFormRef.value.selectedDays &&
            addServiceFormRef.value.selectedDays.filter((day) => day).length ===
              0
          ) {
            addServiceFormRef.value.gatherServiceDataForPreview();
            currentStep.value = "preview";
          } else {
            currentStep.value = "customization";
          }
        } else if (currentStep.value === "customization") {
          addServiceFormRef.value.gatherServiceDataForPreview();
          currentStep.value = "preview";
        }
      }
    };

    const goToPreviousStep = () => {
      console.log(
        `Going to previous step from ${currentStep.value} in ${overlayType.value} form`,
      );

      // Each form type has its own navigation flow
      if (overlayType.value === "Product") {
        // Product form: preview -> initial
        if (currentStep.value === "preview") {
          currentStep.value = "initial";
        }
      } else if (overlayType.value === "Service") {
        // Service form has 3 steps: initial -> availability -> customization -> preview
        if (currentStep.value === "customization") {
          currentStep.value = "availability";
        } else if (currentStep.value === "availability") {
          currentStep.value = "initial";
        } else if (currentStep.value === "preview") {
          currentStep.value = "customization"; // Go back to customization from preview
        }
      } else if (overlayType.value === "Multimedia") {
        // Multimedia form: preview -> initial
        if (currentStep.value === "preview") {
          currentStep.value = "initial";
        }
      } else if (overlayType.value === "Media") {
        // Media form only has two steps: initial and preview
        if (currentStep.value === "preview") {
          currentStep.value = "initial";
          // Also reset PostComposer internal view mode back to edit
          try {
            postComposerRef.value?.backToEdit?.();
          } catch {}
        }
      }
    };

    const shouldShowBackArrow = computed(() => {
      console.log(
        `Computing shouldShowBackArrow: overlay=${overlayType.value}, step=${currentStep.value}`,
      );

      // All forms should show back arrow on any step except initial
      // This is consistent across all form types
      return currentStep.value !== "initial";
    });

    const saveProduct = async () => {
      isSaveLoading.value = true;
      if (addProductFormRef.value) {
        try {
          await addProductFormRef.value.addOrUpdateProduct();
          emit("itemEdited");
        } catch (error) {
          console.error("Error saving product:", error);
        } finally {
          isSaveLoading.value = false;
        }
      } else {
        console.error("Form reference is not correctly set.");
        isSaveLoading.value = false;
      }
    };

    const saveService = async () => {
      isSaveLoading.value = true;
      if (addServiceFormRef.value) {
        try {
          await addServiceFormRef.value.addOrUpdateService();
          emit("itemEdited");
        } catch (error) {
          console.error("Error saving service:", error);
        } finally {
          isSaveLoading.value = false;
        }
      } else {
        console.error("Form reference is not correctly set.");
        isSaveLoading.value = false;
      }
    };

    const saveMultimedia = async () => {
      isSaveLoading.value = true;
      if (addMultimediaFormRef.value) {
        try {
          await addMultimediaFormRef.value.submitPost();
        } catch (error) {
          console.error("Error saving multimedia post:", error);
          toast.error("Failed to save multimedia post: " + error.message);
        } finally {
          isSaveLoading.value = false;
        }
      } else {
        console.error("Multimedia form reference is not correctly set.");
        isSaveLoading.value = false;
      }
    };

    const handleStepUpdate =
      (formType) =>
      ({ form, step }) => {
        if (form === formType) {
          console.log(`Updating currentStep for ${formType}:`, step);
          // Make sure we don't accept invalid steps for Media form
          if (
            formType === "media" &&
            step !== "initial" &&
            step !== "preview"
          ) {
            console.warn(
              `Invalid step '${step}' for Media form, defaulting to preview`,
            );
            currentStep.value = "preview";
          } else {
            currentStep.value = step;
          }
        }
      };

    const skipToPreview = () => {
      if (addServiceFormRef.value) {
        if (addServiceFormRef.value.customAvailability) {
          addServiceFormRef.value.customAvailability.value = [];
        }
        if (addServiceFormRef.value.selectedSlots) {
          addServiceFormRef.value.selectedSlots.value = new Array(7)
            .fill([])
            .map(() => []);
        }
      }
      currentStep.value = "preview";
    };

    watch(showOverlay, (newValue) => {
      if (newValue) {
        document.body.classList.add("no-scroll");
      } else {
        document.body.classList.remove("no-scroll");
      }
    });

    watch(overlayType, (newVal) => {
      console.log("DEBUG: overlayType changed to", newVal);
    });

    watch(showOverlay, (newVal) => {
      console.log("DEBUG: showOverlay changed to", newVal);
    });

    watch(currentStep, (newStep) => {
      console.log(`DEBUG: currentStep changed to ${newStep}`);
      console.log("Overlay Type:", overlayType.value);

      if (newStep === "preview") {
        if (overlayType.value === "Service") {
          console.log(
            "DEBUG: Calling gatherServiceDataForPreview in SellingAction.vue",
          );
          addServiceFormRef.value?.gatherServiceDataForPreview();
        } else if (overlayType.value === "Multimedia") {
          console.log(
            "DEBUG: Calling gatherMultimediaDataForPreview in SellingAction.vue",
          );
          addMultimediaFormRef.value?.gatherMultimediaDataForPreview();
        }
      }
    });

    // Open Product Overlay
    const openProductOverlay = (product = null) => {
      // Set the appropriate title based on whether we're editing or creating
      if (product && product.id) {
        currentProduct.value = product;
        dynamicTitleProduct.value = "Edit Product Details";
      } else {
        currentProduct.value = null;
        dynamicTitleProduct.value = "Add Product";
      }
      openOverlay("Product");
    };

    // Open Service Overlay
    const openServiceOverlay = (service = null) => {
      // Set the appropriate title based on whether we're editing or creating
      if (service && service.id) {
        currentProduct.value = service;
        dynamicTitleService.value = "Edit Service Details";
      } else {
        currentProduct.value = null;
        dynamicTitleService.value = "Add Service";
      }
      openOverlay("Service");
    };

    return {
      selectedCard,
      selectCard,
      handleProductAdded,
      handleServiceAdded,
      dynamicTitle,
      dynamicTitleProduct,
      dynamicTitleService,
      mediaOverlayTitle,
      postingAsLabel,
      currentStep,
      showOverlay,
      overlayType,
      openOverlay,
      openBatchMintModal,
      closeOverlay,
      isSaveLoading,
      goToPreviousStep,
      goToNextStep,
      shouldShowBackArrow,
      handleTitleUpdate,
      handleMediaTitleUpdate,
      saveProduct,
      saveService,
      saveMultimedia,
      addProductFormRef,
      addServiceFormRef,
      triggerAddProduct,
      triggerAddService,
      validateFormAndProceed,
      canPreview,
      handleStepUpdate,
      skipToPreview,
      openProductOverlay,
      openServiceOverlay,
      closeMenu,
      openMultimediaOverlay,
      handleMultimediaAdded,
      triggerAddMultimedia,
      addMultimediaFormRef,
      handleMediaAdded,
      handleCaptionUpdate,
      saveMedia,
      openMediaOverlay,
      postComposerRef,
      composerKey,
      mediaLoading,
      currentProduct,
      handleOpenSale,
    };
  },
};
</script>

<style scoped>
.SellingAction {
  overflow: visible;
}

.SellingAction.hidden {
  pointer-events: none;
  overflow: visible;
}

.SellingAction.hidden > * {
  pointer-events: auto;
}

.SellingAction .row.alignCentreRow > .unit-button {
  height: var(--control-height-default);
  padding: 0 var(--space-6);
  border-color: var(--color-border-control);
  font-weight: var(--font-weight-normal);
}

.SellingAction .row.alignCentreRow > .unit-button:not(:disabled):hover {
  background: transparent;
  transform: translateY(var(--motion-pressed-offset));
}

/* Dropdown panel — matches Dropdown.vue .text-area.compact */
.actions-menu {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 140px;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-canvas);
  box-shadow: 0 2px 6px var(--color-shadow-default);
}

/* Menu items — matches MasonryMarketplace .actions-menu .menu-item */
.menu-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  font-size: var(--font-size-label);
  border-radius: var(--radius-sm);
}

.menu-item:hover {
  background-color: var(--color-surface-menu-hover);
}

.menu-item.pressed {
  background-color: var(--color-surface-menu-hover);
}

.menu-item svg.tinyImg {
  flex-shrink: 0;
}
</style>
